// @wc-ignore-file
// CR-235: pure client-side rules for the files a contributor attaches to a
// suggestion. Every string in here is DATA (MIME types, a bucket name), never
// UI copy — excluded from wuchale so a locale cannot translate a MIME type.

import {
  CR_SUGGESTION_UPLOAD_ACCEPTED_MIME_TYPES,
  RASTER_IMAGE_MIME_TYPES,
} from "@shared/mime-types";
import type { UploadBucketRules } from "./api/cr-uploads-storage";
import {
  FileTooLargeError,
  UnsupportedFileError,
  type UploadFilePick,
  type UploadFileRefusalCode,
  pickUploadFiles,
} from "./cr-uploads";

/** Private bucket created by 20261021120000_cr235_suggestion_attachments.sql. */
export const CR_SUGGESTION_UPLOAD_BUCKET = "cr-suggestion-uploads";

/** Mirrors the bucket's `file_size_limit` (50 MiB); Storage and the RPC re-check it. */
export const CR_SUGGESTION_UPLOAD_MAX_SIZE_BYTES = 50 * 1024 * 1024;

/** Mirrors `cr_suggestion_attachment_cap()` (the RPC refuses the 11th file). */
export const CR_SUGGESTION_ATTACHMENT_MAX_FILES = 10;

/**
 * Extensions a browser may leave untyped. Only the bucket's own types: the
 * resolved type is stored as the object's Content-Type, which the bucket and
 * `add_my_cr_suggestion_attachment` check again.
 */
const EXTENSION_MIME_FALLBACK: Readonly<Record<string, string>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
};

/** The file picker's `accept`: the bucket's MIME types plus their extensions. */
export const CR_SUGGESTION_UPLOAD_ACCEPT_ATTRIBUTE = [
  ...CR_SUGGESTION_UPLOAD_ACCEPTED_MIME_TYPES,
  ...Object.keys(EXTENSION_MIME_FALLBACK).map((extension) => `.${extension}`),
].join(",");

/** The Content-Type the file is stored under: the browser's label, else the extension's. */
export function resolveCrSuggestionUploadMimeType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME_FALLBACK[extension] ?? "";
}

export function isCrSuggestionUploadSupported(file: File): boolean {
  return CR_SUGGESTION_UPLOAD_ACCEPTED_MIME_TYPES.has(resolveCrSuggestionUploadMimeType(file));
}

/**
 * Why the file cannot be attached, or `null` when it can. The error classes
 * are CR-147's; on this path `crSuggestionWriteErrorCode` (snake_case codes)
 * and `pickSuggestionFiles` map them, never CR-147's `crUploadErrorReason`.
 */
export function checkCrSuggestionFile(file: File): UnsupportedFileError | FileTooLargeError | null {
  if (!isCrSuggestionUploadSupported(file)) {
    return new UnsupportedFileError(resolveCrSuggestionUploadMimeType(file));
  }
  if (file.size > CR_SUGGESTION_UPLOAD_MAX_SIZE_BYTES) {
    return new FileTooLargeError(file.size);
  }
  return null;
}

/** The bucket and its client-side rules, for `uploadToCrUploadsBucket`. */
export const CR_SUGGESTION_UPLOAD_RULES: UploadBucketRules = {
  bucket: CR_SUGGESTION_UPLOAD_BUCKET,
  resolveMimeType: resolveCrSuggestionUploadMimeType,
  check: checkCrSuggestionFile,
};

/**
 * Whether the signed URL may render the file in the tab (raster images) rather
 * than download it (PDFs, videos): plan S2.6, the CR-147 caution about MIME
 * labels set by the uploader's browser. `download` is not signed, so what
 * really keeps the bucket safe is its allow-list: never add a scriptable type
 * (HTML, SVG, …) to `cr-suggestion-uploads`.
 */
export function isCrSuggestionUploadInline(mimeType: string): boolean {
  return RASTER_IMAGE_MIME_TYPES.has(mimeType);
}

/** Why a picked file was not queued (a subset of the page's write error codes). */
export type CrSuggestionFileRefusalCode = UploadFileRefusalCode;

/**
 * CR-235 S2.5/S2.7: split a pick into the files to upload and the ones refused
 * client-side, in pick order. `alreadyCount` is what the suggestion (or the
 * dialog's queue) already holds; past the 10-file cap the rest is refused.
 */
export function pickSuggestionFiles(picked: readonly File[], alreadyCount: number): UploadFilePick {
  return pickUploadFiles(
    picked,
    alreadyCount,
    CR_SUGGESTION_ATTACHMENT_MAX_FILES,
    checkCrSuggestionFile,
  );
}
